import React, { useState } from 'react';
import { Plus, AlertCircle } from 'lucide-react';
import { trpc } from '@lib/trpc';
import { LoadingSpinner, Button } from '@atoms';
import { SearchInput } from '@molecules';
import { UserTable, AdminUserModal } from '@organisms/admin';
import type { User } from '@organisms/admin';
import toast from 'react-hot-toast';


export const AdminUsersTab: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Get tRPC utils for query invalidation
  const utils = trpc.useUtils();

  // tRPC queries and mutations
  const { 
    data: usersData, 
    isLoading, 
    error, 
    refetch 
  } = trpc.admin.getAllUsers.useQuery();

  const deleteUserMutation = trpc.admin.deleteUser.useMutation({
    onSuccess: () => {
      toast.success('User deleted successfully');
      // Invalidate user queries to refresh data everywhere
      utils.admin.getAllUsers.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete user');
    },
  });

  const users = usersData?.data || [];

  // Filter users based on search term
  // Note: Access control (admin user visibility) is now handled at the backend level
  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleCreateUser = () => {
    setSelectedUser(null);
    setIsEditing(false);
    setShowUserModal(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsEditing(true);
    setShowUserModal(true);
  };

  const handleDeleteUser = async (user: User) => {
    if (window.confirm(`Are you sure you want to delete ${user.email}?`)) {
      await deleteUserMutation.mutateAsync({ id: user.id });
    }
  };

  const handleModalClose = () => {
    setShowUserModal(false);
    setSelectedUser(null);
    setIsEditing(false);
  };
  const handleModalSuccess = () => {
    refetch();
    handleModalClose();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Loading users..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-12 h-12 text-error-500 mb-4" />
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          Failed to load users
        </h3>
        <p className="text-text-secondary mb-4">
          {error.message || 'An unexpected error occurred'}
        </p>
        <Button onClick={() => refetch()} variant="primary">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">User Management</h2>
          <p className="text-text-secondary mt-1">
            Manage user accounts, roles, and permissions
          </p>
        </div>
        <Button
          onClick={handleCreateUser}
          variant="primary"
          icon={Plus}
        >
          Add User
        </Button>
      </div>

      {/* Search */}
      <SearchInput
        placeholder="Search users..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="max-w-md"
      />

      {/* Users Table */}
      <UserTable
        users={filteredUsers.length === 0 && searchTerm ? [] : filteredUsers}
        onEditUser={handleEditUser}
        onDeleteUser={handleDeleteUser}
        deletingUserId={deleteUserMutation.isPending ? selectedUser?.id : undefined}
      />
      {/* Summary */}
      <div className="flex justify-between text-sm text-text-secondary">
        <span>
          Showing {filteredUsers.length} of {users.length} users
        </span>
        <span>
          {users.filter(u => u.isActive).length} active, {users.filter(u => !u.isActive).length} inactive
        </span>
      </div>

      {/* Modals */}
      {showUserModal && (
        <AdminUserModal
          user={selectedUser}
          isEditing={isEditing}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

    </div>
  );
};
